import { computed, defineComponent, reactive } from "vue";
import * as Sentry from "@sentry/vue";
import Section from "components/section";
import { HeaderMd } from "components/headers/md";
import { Form } from "@/form/useForm/components/form";
import { useMemoAsync } from "@/utils/async/useMemoAsync";
import IRClient from "@/modules/ir/apiClient";
import Avatar from "components/avatar";
import Button from "components/button";
import { useToasts } from "components/toast/hooks";
import { useUserStore } from "@/stores/user";
import { showPrompt } from "components/modal/prompt";
import type { IAttachment, TDocRef } from "findef";
import HeaderSm from "components/headers/sm";
import { getUserFullName } from "@/utils/user";
import { useSession } from "@/modules/auth/hooks/useSession";
import { boxedRef } from "@/types/boxedRef";
import { SettingsCard } from "@/modules/settings/components/settings-card";
import { useProfileForm, type ProfileFormState } from "@/modules/settings/forms/profileForm";
import { useUserSettings } from "@/modules/settings/hooks/useUserSettings";


export default defineComponent(() => {
  definePageMeta({
    layout: 'settings',
    name: 'Profile'
  })

  const userStore = useUserStore();
  const session = useSession();

  const userFetch = useMemoAsync(async () => {
    return await IRClient.whoami();
  });

  const user = computed(() => {
    if (userFetch.loading || userFetch.error) return undefined;
    return userFetch.data;
  });

  const state = reactive({
    loading: false,
  });

  const placeholderImage = computed(() => {
    const u = user.value;
    if (!u) return undefined;
    return (
      <Avatar
        fullname={getUserFullName(u)}
        colorPreference={u.color}
        round="none"
        size="full"
      />
    );
  });

  const form = useProfileForm({
    imageFallback: boxedRef(placeholderImage)
  });

  const initialFormState = computed((): ProfileFormState => {
    return {
      profilePicture: {
        attachment: user.value?.avatar as TDocRef<IAttachment>,
      },
      countryAndEmail: {
        email: user.value?.email || "",
        country: user.value?.country || "SE",
      },
      name: {
        firstname: user.value?.firstname || "",
        lastname: user.value?.lastname || "",
      },
      currency: user.value?.currency || "SEK",
    };
  });

  form.watchState(initialFormState, undefined, true);


  const beforeRouteLeave = async (_to: any, _from: any, next: any) => {
    if (form.hasChanges.value) {
      const shouldLeave = await showPrompt({
        title: "Unsaved Changes",
        body: `You have unsaved changes. Are you sure you want to leave?`,
        answerNoText: "No, Stay",
        answerYesText: "Yes, Leave",
      });

      if (shouldLeave) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  };

  onBeforeRouteLeave(beforeRouteLeave);

  const toastStore = useToasts();
  const settings = useUserSettings();

  const handleSaveChanges = async () => {
    state.loading = true;
    const fdata = form.cast();

    try {
      settings.setCurrency(fdata.currency);
      const updatedUser = await IRClient.updateUserProfile({
        firstname: fdata.name.firstname,
        lastname: fdata.name.lastname,
        avatar: fdata.profilePicture?.attachment,
        country: fdata.countryAndEmail.country,
        currency: fdata.currency,
      });

      form.markAsSaved();

      if (updatedUser) {
        session.patchLocalUser((user) => ({
          ...user,
          avatar: updatedUser.avatar
        }))
      }

      await userStore.refreshUser();
      toastStore.pushToast({
        title: "Success",
        subtitle: "User profile updated",
        variant: "success",
      });
      userFetch.refresh();
      settings.refresh();
    } catch (e) {
      Sentry.captureException(e);
      form.addForcedChange();
      toastStore.pushToast({
        title: "Error",
        subtitle: "Could not update data, try again later",
        variant: "warning",
      });
    }

    state.loading = false;
  };

  return () => {
    return (
        <div class="w-full h-full flex flex-col gap-4">
          <HeaderSm
            title="Profile"
            subtitle="Change the settings for everything concerning your profile information"
          />
          <SettingsCard loading={userFetch.loading}>
            <Section>
              <HeaderMd
                title="User Information"
                subtitle="Information about you as a user and your preferences"
              />
            </Section>
            <Section class="w-full">
              <Form form={form} />

              <Section class="mt-4 flex justify-end">
                <Button
                  disabled={!form.hasChanges.value}
                  isLoading={state.loading}
                  onClick={handleSaveChanges}
                >
                  Save Changes
                </Button>
              </Section>
            </Section>
          </SettingsCard>
        </div>
      );
  }
})
